/* global globalThis */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import DcsdDialog from "../../components/modals/DcsdDialog";
import {
    EXPIRY_BUFFER_MILLI,
    EXPIRY_PROMPT_COUNTDOWN_MILLI,
    SAML_LOGOUT_URL,
    SERVICE_HOST,
    START_SESSION_URL,
    TOKEN_EXPIRY_CHECK_MILLI,
    TOKEN_URL,
    TOKEN_URL_DEV,
    SYNC_COOKIE_NAME
} from "./config";
import { GlobalContext } from "../../components/contextProvider/ContextProvider";
import { getDateFromEpoch } from "../DateFormatter";
import UserDetails from "../UserDetails";

/**
 * Token Object that lives in memory.
 */
let inMemoryToken;
let isLoggingOut = false;
let retrievedFlag = true;
let isPromptOpen = false;
let refreshInProgress = false;

const getCookieDomain = () => {
    const { hostname } = globalThis.location;
    if (hostname === "localhost" || /^[0-9.]+$/.test(hostname)) {
        return "";
    }
    const parts = hostname.split(".");
    if (parts.length > 2) {
        return `.${parts.slice(-2).join(".")}`;
    }
    return `.${hostname}`;
};

const setSyncCookie = (action) => {
    const domain = getCookieDomain();
    const domainStr = domain ? `; domain=${domain}` : "";
    const value = `${action}:${Date.now()}`;
    document.cookie = `${SYNC_COOKIE_NAME}=${value}; path=/${domainStr}; max-age=300; SameSite=Lax; Secure`;
    return value;
};

const getSyncCookie = () => {
    const nameEQ = `${SYNC_COOKIE_NAME}=`;
    const ca = document.cookie.split(";");
    for (const rawC of ca) {
        const c = rawC.trim();
        if (c.startsWith(nameEQ)) return c.substring(nameEQ.length, c.length);
    }
    return null;
};

let triggerPromptCallback = null;

const register401Handler = (callback) => {
    triggerPromptCallback = callback;
};

const trigger401Prompt = () => {
    if (triggerPromptCallback) {
        triggerPromptCallback();
    }
};

/**
 * This is to store the inMemoryToken Object.
 * @name storeToken
 * @param jwtToken
 * @param expiryEpochMilli
 * @param expiryDate
 * @param tokenType
 * @param username
 * @param guid
 */
const storeToken = ({ jwt, expiryEpochMilli, expiryDate, tokenType, username, guid }) => {
    inMemoryToken = {
        token: jwt,
        expiry: expiryEpochMilli < 1000000000000 ? Number.parseInt(expiryEpochMilli, 10) * 1000 : expiryEpochMilli,
        readableExpiry: expiryDate,
        username,
        guid,
        tokenType
    };
};

const clearToken = () => {
    inMemoryToken = null;
};

/**
 * Obliterate inMemoryToken and user_details session
 * @name fakeLogout
 * @returns {Promise<void>}
 */
const fakeLogout = async () => {
    isPromptOpen = false;
    refreshInProgress = false;
    clearToken();
    sessionStorage.removeItem("user_details");
};

/**
 * Logout function that logs people out of active tabs and redirect logout for SAML.
 * @name logout
 * @returns {Promise<void>}
 */
async function logout() {
    isLoggingOut = true;
    isPromptOpen = false;
    refreshInProgress = false;
    clearToken();
    // remove all sessionStorage
    sessionStorage.clear();
    sessionStorage.setItem("logout", Date.now());
    globalThis.location.replace(SAML_LOGOUT_URL);
}

/**
 * This is to capture specific responses from API calls.
 */
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response) {
            if (error.response.status === 401) {
                if (inMemoryToken) {
                    trigger401Prompt();
                } else {
                    globalThis.location.replace(START_SESSION_URL);
                }
            }
            return Promise.reject(error.response);
        }
        return Promise.reject(error);
    }
);

/**
 * Manually decode a token into our format
 * @name parseJwt
 * @param {string} token
 * @return {{}} jsonPayload
 */
const parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replaceAll("-", "+").replaceAll("_", "/");
    const jsonPayload = decodeURIComponent(
        globalThis
            .atob(base64)
            .split("")
            .map((c) => {
                const sliceString = `00${c.codePointAt(0).toString(16)}`.slice(-2);
                return `%${sliceString}`;
            })
            .join("")
    );

    return JSON.parse(jsonPayload);
};

/**
 * This uses Axios library to call the token service.
 * @name retrieveTokenDev
 * @param {number} id
 * @returns {Promise<*>} {@link inMemoryToken}
 */
const retrieveTokenDev = async (id, user) => {
    if (inMemoryToken) {
        return inMemoryToken;
    }
    const uName = user || sessionStorage.getItem("uname");
    const options = {
        headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            "Access-Control-Allow-Origin": SERVICE_HOST
        },
        // This will pass the HTTP Only cookie we need to pass REST API.
        withCredentials: true
    };
    retrievedFlag = false;
    await axios
        .get(`${TOKEN_URL_DEV}/${uName}/token`, options)
        .then((response) => {
            if (response.status === 401) {
                clearInterval(id);
            } else {
                retrievedFlag = true;
                const parsed = parseJwt(response.data);
                const myObj = {
                    jwt: response.data,
                    username: parsed.username,
                    guid: parsed.guid,
                    expiryEpochMilli: parsed.exp,
                    expiryDate: getDateFromEpoch(parsed.exp),
                    tokenType: "REFRESH"
                };
                storeToken(myObj);
            }
        })
        .catch((error) => {
            console.error(error);
        });

    return inMemoryToken;
};

/**
 * This uses Axios library to call the token service.
 * @name retrieveToken
 * @param {number} id
 * @returns {Promise<*>} {@link inMemoryToken}
 */
const retrieveToken = async (id) => {
    const options = {
        headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            "Access-Control-Allow-Origin": SERVICE_HOST
        },
        // This will pass the HTTP Only cookie we need to pass REST API.
        withCredentials: true
    };
    retrievedFlag = false;
    await axios
        .get(TOKEN_URL, options)
        .then((response) => {
            if (response.status === 401) {
                clearInterval(id);
            } else {
                retrievedFlag = true;
                storeToken(response.data.payload);
            }
        })
        .catch((error) => {
            console.error(error);
        });

    return inMemoryToken;
};

/**
 * Refresh the token, so that it doesn't expire
 * @name Auth
 * @return {JSX.Element | null}
 * @constructor
 */
const Auth = () => {
    const [dispatchState, setDispatchState] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);

    const { dispatch, state } = useContext(GlobalContext);
    const { username } = state || {};
    const userDetails = UserDetails();
    const isFetching = useRef(false);
    const intervalId = useRef(null);
    const promptTimeoutId = useRef(null);
    const lastProcessedAction = useRef(getSyncCookie());

    const triggerPrompt = (fromSync = false) => {
        if (!isPromptOpen) {
            isPromptOpen = true;
            setShowPrompt(true);
            refreshInProgress = true;

            if (!fromSync) {
                lastProcessedAction.current = setSyncCookie("show_prompt");
            }

            promptTimeoutId.current = setTimeout(() => {
                setShowPrompt(false);
                isPromptOpen = false;
                lastProcessedAction.current = setSyncCookie("logout");
                logout();
            }, EXPIRY_PROMPT_COUNTDOWN_MILLI);
        }
    };

    const handleContinue = useCallback(
        (fromSync = false) => {
            setShowPrompt(false);
            isPromptOpen = false;
            refreshInProgress = false;
            if (promptTimeoutId.current) {
                clearTimeout(promptTimeoutId.current);
                promptTimeoutId.current = null;
            }

            if (!fromSync) {
                lastProcessedAction.current = setSyncCookie("refresh");
            }

            isFetching.current = true;

            const isDevLogin = sessionStorage.getItem("devLogin") === "devLogin";
            const fetchDev = () => retrieveTokenDev(intervalId.current, username);
            const fetchProd = () => retrieveToken(intervalId.current);
            const fetchToken = isDevLogin ? fetchDev : fetchProd;

            if (retrievedFlag) {
                fetchToken()
                    .then((response) => {
                        inMemoryToken = response;
                        setDispatchState(inMemoryToken);
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(() => {
                        isFetching.current = false;
                    });
            }
        },
        [username]
    );

    const handleCancel = () => {
        setShowPrompt(false);
        isPromptOpen = false;
        refreshInProgress = false;
        if (promptTimeoutId.current) {
            clearTimeout(promptTimeoutId.current);
            promptTimeoutId.current = null;
        }
        lastProcessedAction.current = setSyncCookie("logout");
        logout();
    };

    // Register global 401 callback
    useEffect(() => {
        register401Handler(() => {
            if (inMemoryToken && (inMemoryToken.expiry || 0) < Date.now()) {
                logout();
            } else {
                triggerPrompt(false);
            }
        });
        return () => {
            register401Handler(() => {});
        };
    }, []);

    // Background Listener: polls every 5000ms when prompt is NOT open
    useEffect(() => {
        let checkPromptSync = null;

        if (!showPrompt) {
            checkPromptSync = setInterval(() => {
                const actionVal = getSyncCookie();
                if (actionVal && actionVal !== lastProcessedAction.current) {
                    lastProcessedAction.current = actionVal;
                    const action = actionVal.split(":")[0];
                    if (action === "show_prompt") {
                        triggerPrompt(true);
                    } else if (action === "logout") {
                        logout();
                    }
                }
            }, 5000);
        }

        return () => {
            if (checkPromptSync) {
                clearInterval(checkPromptSync);
            }
        };
    }, [showPrompt]);

    // Active Listener: polls every 1000ms when prompt IS open
    useEffect(() => {
        let checkSyncCookie = null;

        if (showPrompt) {
            checkSyncCookie = setInterval(() => {
                const actionVal = getSyncCookie();
                if (!actionVal || actionVal === lastProcessedAction.current) {
                    return;
                }

                lastProcessedAction.current = actionVal;
                const action = actionVal.split(":")[0];

                if (action !== "refresh" && action !== "logout") {
                    return;
                }

                if (checkSyncCookie) {
                    clearInterval(checkSyncCookie);
                }
                if (promptTimeoutId.current) {
                    clearTimeout(promptTimeoutId.current);
                    promptTimeoutId.current = null;
                }
                setShowPrompt(false);
                isPromptOpen = false;
                refreshInProgress = false;

                if (action === "refresh") {
                    // Execute token refresh locally without setting the cookie again
                    handleContinue(true);
                } else if (action === "logout") {
                    logout();
                }
            }, 1000);
        }

        return () => {
            if (checkSyncCookie) {
                clearInterval(checkSyncCookie);
            }
        };
    }, [showPrompt, handleContinue]);

    // Focus/Visibility Listener: syncs cookie immediately when tab becomes active/visible
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== "visible") return;

            if (inMemoryToken && (inMemoryToken.expiry || 0) < Date.now()) {
                logout();
                return;
            }

            const actionVal = getSyncCookie();
            if (!actionVal || actionVal === lastProcessedAction.current) return;

            lastProcessedAction.current = actionVal;
            const action = actionVal.split(":")[0];

            if (action === "show_prompt") {
                triggerPrompt(true);
                return;
            }

            if (action === "refresh" || action === "logout") {
                if (promptTimeoutId.current) {
                    clearTimeout(promptTimeoutId.current);
                    promptTimeoutId.current = null;
                }
                setShowPrompt(false);
                isPromptOpen = false;
                refreshInProgress = false;

                if (action === "refresh") {
                    handleContinue(true);
                } else {
                    logout();
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleVisibilityChange);
        };
    }, [showPrompt, handleContinue]);

    useEffect(() => {
        const roleDtos = userDetails?.roleDtos;
        const isEmployee = Array.isArray(roleDtos) && roleDtos.find((r) => r.role === "EMPLOYEE");

        const getFetcher = () => {
            const isDevLogin = sessionStorage.getItem("devLogin") === "devLogin";
            const fetchDev = () => retrieveTokenDev(intervalId.current, username);
            const fetchProd = () => retrieveToken(intervalId.current);
            return isDevLogin ? fetchDev : fetchProd;
        };

        const doFetch = () => {
            isFetching.current = true;
            getFetcher()()
                .then((response) => {
                    inMemoryToken = response;
                    setDispatchState(response);
                })
                .catch((e) => {
                    console.error(e);
                })
                .finally(() => {
                    isFetching.current = false;
                });
        };

        const checkToken = () => {
            if (isLoggingOut || isFetching.current || refreshInProgress) return;
            const now = Date.now();

            if (!inMemoryToken) {
                if (retrievedFlag) doFetch();
                else setDispatchState(inMemoryToken);
                return;
            }

            const { expiry } = inMemoryToken;

            if (isEmployee && (expiry || 0) < now) {
                clearInterval(intervalId.current);
                logout();
                return;
            }

            // call token service if token is approaching expiry
            if (Number(expiry) - Number(EXPIRY_BUFFER_MILLI) < now) {
                if (isEmployee) {
                    triggerPrompt(false);
                    return;
                }
                // Parents: silent auto-refresh
                if (retrievedFlag) doFetch();
            }
        };

        // Run immediately on mount, then on interval
        checkToken();
        intervalId.current = setInterval(checkToken, TOKEN_EXPIRY_CHECK_MILLI);

        return () => {
            clearInterval(intervalId.current);
        };
    }, [userDetails, username]);

    /**
     * Retrieve the token and set it in Global Context
     */
    useEffect(() => {
        const runDispatch = (token) => {
            if (token && typeof dispatch === "function") {
                dispatch({ type: "Token", token: token.token });
                dispatch({ type: "Username", username: token.username });
                dispatch({ type: "Guid", guid: token.guid });
            }
        };
        runDispatch(dispatchState || inMemoryToken);
    }, [dispatch, dispatchState]);

    const modalActions = (
        <>
            <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => handleContinue(false)}>
                Continue
            </button>
        </>
    );

    return (
        <DcsdDialog
            actions={modalActions}
            ariaLabel="Session Expiry Warning"
            hasCloseX={false}
            id="session-expiry-dialog"
            onHide={handleCancel}
            open={showPrompt ? "session-expiry-dialog" : null}
            title="Notice: Session Nearing Expiration"
            backdrop="static"
        >
            <p>Your current session is nearing expiration.</p>
            <p>
                Please click the <strong>Continue</strong> button to refresh your session timeout window. If you do not
                take action before your session expires, you will be logged out of all district-developed applications.
            </p>
        </DcsdDialog>
    );
};

/**
 * This is to just get the inMemoryToken.
 * @name readToken
 * @returns {*} {@link inMemoryToken}
 */
const readToken = () => {
    return inMemoryToken;
};

export { Auth, fakeLogout, logout, readToken, retrieveToken, retrieveTokenDev, register401Handler };
